import { useField } from 'formik';
import styled from 'styled-components';

import Icon from '@klv/atoms/icon';
import {
  FieldGroup, Label, Input, Error,
} from '..';
import type { TColorVariant } from '@klv/shared/styles/types';
import type { ISelectInput } from '../types';
import { useEffect, useRef } from 'react';

export const SelectArrow = styled(Icon)<{ $backgroundColor: TColorVariant; }>`
    position: absolute;
    top: 5rem;
    right: 3rem;
    font-size:1rem;
    user-select: none;
    pointer-events: none;
    color: ${({ theme, $backgroundColor }) => theme.form[$backgroundColor].inputText};
`;
export const SelectFieldGroup = styled(FieldGroup)<{ $backgroundColor: TColorVariant; }>`
  ${({ theme, $backgroundColor }) => `
    ${Input} {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      padding: 1.6rem 5.5rem 1.6rem 1.2rem;
      ${theme.fonts.sohne.regular}
      font-size:1.4rem;
      &:invalid {
        ${theme.fonts.sohne.regular}
        font-size: 1.4rem;
        color: ${theme.form[$backgroundColor].placeholderText};
      }
      option {
        width: 100%;
        max-width: 100%;
      }
    }
  `}
`;

// using the onChange prop was causing the SelectBox to not update values.
const useSelectInputOnValueChange = ({ handleChange, field }: any) => {
  // needed ref as change event was getting called multiple times.
  const fieldValue = useRef(null);

  useEffect(() => {
    if (!handleChange) return;
    if ((field?.value || field?.value === "") && field.value !== fieldValue.current) {
      fieldValue.current = field.value;
      handleChange(field.value);
    }
  }, [field, handleChange]);
};

const SelectInput = ({
  label,
  backgroundColor = 'dark',
  handleChange,
  ...props
}: ISelectInput) => {
  const [field, meta] = useField(props);
  useSelectInputOnValueChange({ field, handleChange });

  return (
    <SelectFieldGroup $backgroundColor={backgroundColor}>
      <SelectArrow $backgroundColor={backgroundColor} iconName="ChevronDown" fillColor="currentColor" />
      <Label $backgroundColor={backgroundColor} htmlFor={props.id || props.name}>{label}</Label>
      <Input $backgroundColor={backgroundColor} as="select" hasError={Boolean(meta.touched && meta.error)} {...field} {...props} />
      {(meta.touched && meta.error) && <Error $backgroundColor={backgroundColor}>{meta.error}</Error>}
    </SelectFieldGroup>
  );
};

export default SelectInput;
